import { type PropsWithChildren, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { startAuthorizeRedirect } from '@/app/router/authorizeRedirect';
import { useAuthStore } from '@/features/auth/store/authStore';

// Gate for routes that require a signed-in user.
// Redirects to identity.signcare.io/auth/authorize (OAuth2 code flow).
// The auth service either:
//   a) issues an auth code silently (SSO cookie present) → redirects back here
//   b) redirects to auth-frontend login page → user logs in → redirects back
//
// The redirect is in useEffect (not inline in render) so React's concurrent
// renderer can abandon renders without triggering side effects.
export function ProtectedRoute({ children }: PropsWithChildren) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) return;

    const from = location.pathname !== '/' ? location.pathname + location.search : '';
    startAuthorizeRedirect(from);
  }, [isAuthenticated, location.pathname, location.search]);

  if (!isAuthenticated) {
    // Show a redirecting state while the useEffect fires window.location.assign.
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <p className="text-sm text-muted-foreground">Redirecting to SignCare…</p>
      </div>
    );
  }

  return <>{children}</>;
}
